import React from 'react';
import { faSquareCheck } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import cs from 'classnames';
import { isEqual } from 'lodash';

import ModuleScene from 'features/briefs/components/atoms/ModuleScene';
import {
  ASPECT_RATIO_OPTIONS,
  FILE_TYPE_OPTIONS,
} from 'features/briefs/components/organisms/BriefMultiPageForm/config';
import LightboxFooter from 'shared/components/atoms/LightboxFooter/LightboxFooter';
import { Box, Flex, Text } from 'shared/components/display';
import { CarouselItem } from 'shared/components/molecules/CarouselItem';
import { DefinitionList, ListDetail } from 'shared/components/molecules/DefinitionList';
import { Lightbox } from 'shared/components/organisms/Lightbox';
import Richtext, { RICHTEXT_DEFAULT_VALUE } from 'shared/components/Richtext';

import COLORS from 'shared/styles/colors';
import { Module } from 'shared/typings/briefs';

import styles from './styles';

interface Props {
  module: Module;
}

const BriefModule: React.FC<Props> = ({ module }) => {
  const [lightbox, setLightbox] = React.useState(false);

  const onRequestClose = () => {
    setLightbox(false);
  };

  return (
    <Box css={styles}>
      <DefinitionList className="brief-module" labelColor={COLORS.NEUTRAL800}>
        <ListDetail text="Module Name" labelClassName="brief-module__heading" detailsClassName="brief-module__section">
          {module.name}
        </ListDetail>
        <ListDetail
          text="Module Description"
          labelClassName="brief-module__heading"
          detailsClassName="brief-module__section"
        >
          <Richtext value={JSON.parse(module.content)} readOnly key={`${module.id}-description`} />
        </ListDetail>
        <ListDetail
          hide={!module.exampleContent}
          text="Example Content"
          labelClassName="brief-module__heading"
          detailsClassName="brief-module__section--mt"
        >
          <Flex className="brief-module__file">
            <img
              src={module.exampleContent?.thumbnailUrl}
              alt={module.exampleContent?.name}
              className="brief-module__thumbnail"
              onClick={() => setLightbox(true)}
            />
            <Text className={cs('brief-module__file-name', 'endWithEllipsis')}>{module.exampleContent?.name}</Text>
          </Flex>
        </ListDetail>
        <ListDetail
          text="Talking Points/Text Overlay"
          hide={isEqual(RICHTEXT_DEFAULT_VALUE, JSON.parse(module.talkingPoints))}
          labelClassName="brief-module__heading"
          detailsClassName="brief-module__section"
        >
          <Richtext value={JSON.parse(module.talkingPoints)} readOnly key={`${module.id}-talking-points`} />
        </ListDetail>
        <ListDetail text="File Type" labelClassName="brief-module__heading" detailsClassName="brief-module__section">
          <Text as="span">{FILE_TYPE_OPTIONS.find((x) => x.value === module.fileType)?.label}</Text>
        </ListDetail>
        <ListDetail
          text="Scene Breakdown"
          labelClassName="brief-module__heading"
          detailsClassName="brief-module__section--mt"
          hide={!module.scenes?.length}
        >
          <Flex className="brief-module__scene-wrapper">
            {module.scenes?.map((scene, i) => (
              <ModuleScene key={scene.id} scene={scene} sceneNum={scene.order} />
            ))}
          </Flex>
        </ListDetail>
        <ListDetail
          text="Duration"
          labelClassName="brief-module__heading"
          detailsClassName="brief-module__section"
          hide={module.fileType === 'image'}
        >{`${module.minAssetDuration}s - ${module.maxAssetDuration}s`}</ListDetail>
        <ListDetail
          text="Aspect Ratios"
          labelClassName="brief-module__heading"
          detailsClassName="brief-module__section"
          hide={!module.aspectRatios?.length}
        >
          <Flex className="brief-module__aspect-ratio">
            {module.aspectRatios?.map((ratio) => (
              <Flex className="brief-module__aspect-ratio__option">
                <FontAwesomeIcon icon={faSquareCheck} size="lg" />
                {ASPECT_RATIO_OPTIONS.find((option) => option.value === ratio)?.label}
              </Flex>
            ))}
          </Flex>
        </ListDetail>
      </DefinitionList>

      <Lightbox
        ariaLabel="Preview Example Content"
        isOpen={lightbox}
        selectedItemIndex={0}
        onRequestClose={onRequestClose}
      >
        {[
          <CarouselItem
            url={module.exampleContent?.url}
            key={`${module.id}-example-content`}
            thumbnailUrl={module.exampleContent?.thumbnailUrl}
            mimeType={module.exampleContent?.mimeType || ''}
            Footer={
              <LightboxFooter
                captionData={{
                  caption: module.exampleContent?.name || '',
                }}
              />
            }
          />,
        ]}
      </Lightbox>
    </Box>
  );
};

export default BriefModule;
